// Live Delivery Tracking — real-time list of active deliveries with auto-refresh
import { useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Radio,
  RefreshCw,
  AlertCircle,
  Clock,
  MapPin,
  Truck,
  User,
} from "lucide-react";
import { Button, Skeleton } from "@cieda/djayma-ui";
import { formatDistanceToNow, differenceInMinutes } from "date-fns";
import { fr } from "date-fns/locale";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { deliveryApi } from "@/lib/admin-api/delivery";
import type { Delivery } from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LATE_THRESHOLD_MINUTES = 120;

const STATUS_LABELS: Record<string, string> = {
  assigned: "Assigné",
  in_transit: "En transit",
};

const STATUS_BADGE: Record<string, string> = {
  assigned: "bg-blue-100 text-blue-700",
  in_transit: "bg-amber-100 text-amber-800",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isLate(delivery: Delivery): boolean {
  const ref = delivery.estimated_at ?? delivery.updated_at;
  return differenceInMinutes(new Date(), new Date(ref)) > LATE_THRESHOLD_MINUTES;
}

function formatRelative(iso: string): string {
  return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: fr });
}

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

interface DetailModalProps {
  readonly open: boolean;
  readonly delivery: Delivery | null;
  readonly onClose: () => void;
}

function DeliveryDetailModal({ open, delivery, onClose }: DetailModalProps) {
  if (!delivery) return null;
  const late = isLate(delivery);

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={`Livraison — ${delivery.order.public_ref}`}
      size="md"
    >
      <div className="space-y-4">
        {late && (
          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            <Clock className="h-4 w-4 shrink-0" />
            Cette livraison est en retard (&gt; 2h)
          </div>
        )}

        <dl className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <dt className="text-xs font-medium text-stone-500">Client</dt>
              <dd className="mt-0.5 text-sm text-stone-900">
                {delivery.order.customer_name}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-stone-500">Téléphone</dt>
              <dd className="mt-0.5 text-sm text-stone-900">
                {delivery.order.customer_phone}
              </dd>
            </div>
          </div>

          <div>
            <dt className="text-xs font-medium text-stone-500">Adresse</dt>
            <dd className="mt-0.5 text-sm text-stone-900">
              {delivery.address ?? delivery.order.delivery_address}
            </dd>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <dt className="text-xs font-medium text-stone-500">Chauffeur</dt>
              <dd className="mt-0.5 text-sm text-stone-900">
                {delivery.driver?.full_name ?? "—"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-stone-500">Zone</dt>
              <dd className="mt-0.5 text-sm text-stone-900">
                {delivery.zone?.name ?? "—"}
              </dd>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <dt className="text-xs font-medium text-stone-500">Statut</dt>
              <dd className="mt-0.5">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[delivery.status] ?? "bg-stone-100 text-stone-600"}`}
                >
                  {STATUS_LABELS[delivery.status] ?? delivery.status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-stone-500">
                Dernière MAJ
              </dt>
              <dd className="mt-0.5 text-sm text-stone-900">
                {formatRelative(delivery.updated_at)}
              </dd>
            </div>
          </div>

          {delivery.estimated_at && (
            <div>
              <dt className="text-xs font-medium text-stone-500">
                Livraison estimée
              </dt>
              <dd className="mt-0.5 text-sm text-stone-900">
                {new Intl.DateTimeFormat("fr-SN", {
                  dateStyle: "medium",
                  timeStyle: "short",
                }).format(new Date(delivery.estimated_at))}
              </dd>
            </div>
          )}

          {delivery.notes && (
            <div>
              <dt className="text-xs font-medium text-stone-500">Notes</dt>
              <dd className="mt-0.5 text-sm text-stone-900">{delivery.notes}</dd>
            </div>
          )}
        </dl>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Row component
// ---------------------------------------------------------------------------

interface TrackingRowProps {
  readonly delivery: Delivery;
  readonly onClick: () => void;
}

function TrackingRow({ delivery, onClick }: TrackingRowProps) {
  const late = isLate(delivery);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-4 border-b border-stone-100 px-4 py-3.5 text-left transition-colors last:border-b-0 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-300"
    >
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-stone-100">
        <Truck className="h-4 w-4 text-stone-500" />
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-stone-900">
            {delivery.order.public_ref}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[delivery.status] ?? "bg-stone-100 text-stone-600"}`}
          >
            {STATUS_LABELS[delivery.status] ?? delivery.status}
          </span>
          {late && (
            <span className="flex items-center gap-0.5 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
              <Clock className="h-3 w-3" />
              En retard
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-500">
          <span className="flex items-center gap-1">
            <User className="h-3.5 w-3.5" />
            {delivery.driver?.full_name ?? "Non assigné"}
          </span>
          <span className="flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" />
            <span className="max-w-[220px] truncate">
              {delivery.address ?? delivery.order.delivery_address}
            </span>
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {formatRelative(delivery.updated_at)}
          </span>
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function LiveTracking() {
  const [zoneFilter, setZoneFilter] = useState("");
  const [driverFilter, setDriverFilter] = useState("");
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(
    null,
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-live-tracking", zoneFilter, driverFilter],
    queryFn: () =>
      deliveryApi.list({
        limit: 200,
        zone_id: zoneFilter || null,
        driver_id: driverFilter || null,
      }),
    staleTime: 15_000,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  const activeDeliveries = useMemo(
    () =>
      (data?.deliveries ?? []).filter(
        (d) => d.status === "assigned" || d.status === "in_transit",
      ),
    [data?.deliveries],
  );

  const lateCount = useMemo(
    () => activeDeliveries.filter(isLate).length,
    [activeDeliveries],
  );

  const zones = data?.zones ?? [];
  const drivers = data?.drivers ?? [];

  const handleRowClick = useCallback((delivery: Delivery) => {
    setSelectedDelivery(delivery);
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Page header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Radio className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Suivi en temps réel
              </h1>
              <p className="mt-0.5 text-sm text-stone-500">
                {isLoading ? (
                  "Chargement…"
                ) : (
                  <>
                    {activeDeliveries.length} livraison(s) active(s)
                    {lateCount > 0 && (
                      <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                        {lateCount} en retard
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-xs text-stone-400">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
              Actualisation auto. 30s
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={zoneFilter}
            onChange={(e) => setZoneFilter(e.target.value)}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Toutes les zones</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </select>

          <select
            value={driverFilter}
            onChange={(e) => setDriverFilter(e.target.value)}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Tous les chauffeurs</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.full_name}
              </option>
            ))}
          </select>
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les livraisons actives.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Réessayer
            </Button>
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="overflow-hidden rounded-lg border border-stone-200 bg-white">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex items-start gap-4 border-b border-stone-100 px-4 py-3.5 last:border-b-0"
              >
                <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-64" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delivery list */}
        {!isLoading && !isError && (
          <>
            {activeDeliveries.length === 0 ? (
              <EmptyState
                icon={<Truck className="h-6 w-6" />}
                title="Aucune livraison active"
                description="Aucune livraison assignée ou en transit pour le moment."
              />
            ) : (
              <div className="overflow-hidden rounded-lg border border-stone-200 bg-white">
                {activeDeliveries.map((delivery) => (
                  <TrackingRow
                    key={delivery.id}
                    delivery={delivery}
                    onClick={() => handleRowClick(delivery)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail modal */}
      <DeliveryDetailModal
        open={selectedDelivery !== null}
        delivery={selectedDelivery}
        onClose={() => setSelectedDelivery(null)}
      />
    </AdminLayout>
  );
}
